import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Plus, Minus, Settings2, CheckCircle2, Upload, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useCart } from '../context/CartContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const { addToCart, loading: cartLoading } = useCart();
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [useCustomSize, setUseCustomSize] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageBack, setUploadedImageBack] = useState(null); // For dual upload products
  const [selectedQuantityTier, setSelectedQuantityTier] = useState(null); // For quantity pricing
  const [selectedStickerType, setSelectedStickerType] = useState(null); // For stickers product
  const [stickerStock, setStickerStock] = useState('');
  const [stickerCoating, setStickerCoating] = useState('');
  // Bumper stickers state
  const [bumperStock, setBumperStock] = useState('');
  const [bumperLamination, setBumperLamination] = useState('');
  // Roll labels state
  const [rollStock, setRollStock] = useState('');
  const [rollColorspec, setRollColorspec] = useState('');
  const [rollLamination, setRollLamination] = useState('');
  const [rollUnwind, setRollUnwind] = useState('');
  const [rollTurnaround, setRollTurnaround] = useState('');
  
  // Finish options state
  const [finishOptions, setFinishOptions] = useState({
    contourCut: 'No',
    drillHole: 'None',
    roundedCorners: 'No',
    hems: 'No Hems',
    grommets: 'No Grommets',
    threadColor: 'None',
    polePockets: 'No Pole Pockets',
    stock: '3/16" White Gator Board',
    colorSpec: '4/0 (4 color front)',
    whiteMask: 'No',
    pvcGrommets: 'No Grommets',
    pvcRoundedCorners: 'No',
    pvcColorSpec: '4/0 (4 color front)',
    foamCoreColorSpec: '4/0 (4 color front)',
    coroplastColorSpec: '4/4 (4 color both sides)',
    coroplastFluteDirection: 'Vertical',
    coroplastGrommets: 'None',
    coroplastHStakes: 'No',
    coroplastContourCut: 'No',
    sidewalkColorSpec: '4/0 (4 color front)',
    sidewalkFluteDirection: 'Vertical',
    // Scrim Vinyl Banner options
    scrimHems: 'No Hems',
    scrimGrommets: 'No Grommets',
    scrimPolePockets: 'No Pole Pockets',
    scrimDRings: 'No D-Rings',
    scrimVelcro: 'No Velcro',
    scrimRope: 'No Rope',
    // Mesh Banner options
    meshHems: 'No Hems',
    meshGrommets: 'No Grommets',
    meshPolePockets: 'None',
    // Business Card options
    bcShape: 'Rectangle',
    bcCornerRadius: '1/8 inch',
    bcStock: '14PT C2S',
    bcColorSpec: '4/4 (both sides)',
    bcCoating: 'No Coating',
    bcSpotUV: 'No Spot UV',
  });

  const updateFinishOption = (key, value) => {
    setFinishOptions(prev => ({ ...prev, [key]: value }));
  };

  // Check if product needs custom size only (no predefined sizes)
  const isCustomSizeOnly = product.pricePerSqFt && (!product.sizes || product.sizes.length === 0);
  
  // Check if product has both predefined sizes and custom size option
  const hasBothSizeOptions = product.pricePerSqFt && product.sizes && product.sizes.length > 0;

  // Calculate price based on selections
  const calculatedPrice = useMemo(() => {
    if (!product) return null;

    let unitPrice = 0;
    let sizeDisplay = '';

    // Helper function to get Coroplast volume-based price per sq ft
    const getCoroplastPricePerSqFt = (qty) => {
      if (!product.hasVolumeDiscounts || !product.volumePricing) {
        return product.pricePerSqFt;
      }
      const pricing = product.volumePricing[finishOptions.coroplastColorSpec];
      if (!pricing) return product.pricePerSqFt;
      
      if (qty >= 76) return pricing[76];
      if (qty >= 51) return pricing[51];
      if (qty >= 26) return pricing[26];
      if (qty >= 6) return pricing[6];
      return pricing[1];
    };

    // Custom size calculation
    if ((isCustomSizeOnly || useCustomSize) && customWidth && customHeight) {
      const width = parseFloat(customWidth);
      const height = parseFloat(customHeight);
      const area = (width * height) / 144;
      sizeDisplay = `${customWidth}x${customHeight}`;

      // Coroplast with volume pricing
      if (product.category === 'coroplast' && product.hasVolumeDiscounts) {
        const pricePerSqFt = getCoroplastPricePerSqFt(quantity);
        unitPrice = area * pricePerSqFt;
        unitPrice += product.finishOptions?.grommets?.prices?.[finishOptions.coroplastGrommets] || 0;
        unitPrice += product.finishOptions?.hStakes?.prices?.[finishOptions.coroplastHStakes] || 0;
        if (finishOptions.coroplastContourCut === 'Yes') {
          unitPrice *= 1.10;
        }
      }
      // Foam Core with dynamic price per sq ft
      else if (product.category === 'foam-core' && product.finishOptions?.colorSpec?.isPricePerSqFt) {
        const pricePerSqFt = product.finishOptions.colorSpec.prices[finishOptions.foamCoreColorSpec] || product.pricePerSqFt;
        unitPrice = area * pricePerSqFt;
      }
      // Scrim Vinyl Banner finish options
      else if (product.category === 'scrim-vinyl') {
        unitPrice = area * product.pricePerSqFt;
        unitPrice += product.finishOptions?.grommets?.prices?.[finishOptions.scrimGrommets] || 0;
        unitPrice += product.finishOptions?.hems?.prices?.[finishOptions.scrimHems] || 0;
        unitPrice += product.finishOptions?.polePockets?.prices?.[finishOptions.scrimPolePockets] || 0;
        unitPrice += product.finishOptions?.dRings?.prices?.[finishOptions.scrimDRings] || 0;
        unitPrice += product.finishOptions?.velcro?.prices?.[finishOptions.scrimVelcro] || 0;
        unitPrice += product.finishOptions?.rope?.prices?.[finishOptions.scrimRope] || 0;
      }
      // Mesh Banner finish options
      else if (product.category === 'mesh-banner') {
        unitPrice = area * product.pricePerSqFt;
        unitPrice += product.finishOptions?.grommets?.prices?.[finishOptions.meshGrommets] || 0;
        unitPrice += product.finishOptions?.hems?.prices?.[finishOptions.meshHems] || 0;
        unitPrice += product.finishOptions?.polePockets?.prices?.[finishOptions.meshPolePockets] || 0;
      }
      // Standard price per sq ft
      else if (product.pricePerSqFt) {
        unitPrice = area * product.pricePerSqFt;
      }

      // Add finish options for canvas banners (not scrim-vinyl or mesh-banner)
      if (product.finishOptions?.polePockets?.prices && product.category !== 'scrim-vinyl' && product.category !== 'mesh-banner') {
        unitPrice += product.finishOptions.polePockets.prices[finishOptions.polePockets] || 0;
      }

      // Add finish options for Aluminum/Acrylic products
      if (product.finishOptions?.contourCut?.prices && product.category !== 'coroplast') {
        unitPrice += product.finishOptions.contourCut.prices[finishOptions.contourCut] || 0;
        unitPrice += product.finishOptions.drillHoleAndHardware?.prices?.[finishOptions.drillHole] || 0;
        unitPrice += product.finishOptions.roundedCorners?.prices?.[finishOptions.roundedCorners] || 0;
      }

      // Add finish options for PVC
      if (product.category === 'pvc' && product.finishOptions?.grommets?.prices) {
        unitPrice += product.finishOptions.grommets.prices[finishOptions.pvcGrommets] || 0;
        unitPrice += product.finishOptions.roundedCorners?.prices?.[finishOptions.pvcRoundedCorners] || 0;
      }
    }
    // Stickers with dynamic type selection
    else if (product.category === 'stickers' && product.stickerTypes && selectedStickerType && selectedSize && selectedQuantityTier) {
      const stickerType = product.stickerTypes.find(t => t.type === selectedStickerType);
      if (stickerType) {
        const sizeKey = selectedSize.replace(/"/g, '');
        // Check if this type uses "default" pricing or size-specific pricing
        const pricing = stickerType.quantityPricing[sizeKey] || stickerType.quantityPricing['default'];
        if (pricing && pricing[selectedQuantityTier]) {
          unitPrice = pricing[selectedQuantityTier];
          sizeDisplay = selectedSize;
          return {
            unitPrice: parseFloat(unitPrice.toFixed(2)),
            quantity: selectedQuantityTier,
            total: parseFloat(unitPrice.toFixed(2)),
            size: sizeDisplay,
            isQuantityPricing: true,
            stickerType: stickerType.label
          };
        }
      }
    }
    // Bumper Stickers with stock and lamination options
    else if (product.category === 'bumper-stickers' && product.stockOptions && selectedSize && bumperStock && bumperLamination && selectedQuantityTier) {
      const stockOption = product.stockOptions.find(s => s.stock === bumperStock);
      if (stockOption) {
        // Get pricing based on lamination selection
        const pricing = stockOption.quantityPricing[bumperLamination] || stockOption.quantityPricing['default'];
        if (pricing && pricing[selectedQuantityTier]) {
          unitPrice = pricing[selectedQuantityTier];
          sizeDisplay = selectedSize;
          return {
            unitPrice: parseFloat(unitPrice.toFixed(2)),
            quantity: selectedQuantityTier,
            total: parseFloat(unitPrice.toFixed(2)),
            size: sizeDisplay,
            isQuantityPricing: true,
            bumperStock: bumperStock,
            bumperLamination: bumperLamination
          };
        }
      }
    }
    // Roll Labels with complex pricing logic
    else if (product.category === 'roll-labels' && product.stockOptions && product.pricingGroups && 
             selectedSize && rollStock && rollColorspec && rollLamination && rollUnwind && rollTurnaround && selectedQuantityTier) {
      const stockOption = product.stockOptions.find(s => s.stock === rollStock);
      if (stockOption) {
        const pricingGroup = product.pricingGroups[stockOption.pricingGroup];
        if (pricingGroup) {
          let price = 0;
          const hasWhiteMask = rollColorspec === '4/0 with White Mask';
          const hasSilk = rollLamination === 'Silk';
          
          // Determine which pricing to use
          if (hasWhiteMask && hasSilk && pricingGroup.whiteMaskWithSilk) {
            // White Mask + Silk = use special pricing table
            price = pricingGroup.whiteMaskWithSilk[selectedQuantityTier] || 0;
          } else if (hasWhiteMask && pricingGroup.whiteMaskSurcharge) {
            // White Mask without Silk = base price + 5% surcharge
            const basePrice = pricingGroup.base[selectedQuantityTier] || 0;
            price = basePrice * (1 + pricingGroup.whiteMaskSurcharge);
          } else {
            // Regular pricing
            price = pricingGroup.base[selectedQuantityTier] || 0;
          }
          
          if (price > 0) {
            return {
              unitPrice: parseFloat(price.toFixed(2)),
              quantity: selectedQuantityTier,
              total: parseFloat(price.toFixed(2)),
              size: selectedSize,
              isQuantityPricing: true,
              rollStock: rollStock,
              rollColorspec: rollColorspec,
              rollLamination: rollLamination
            };
          }
        }
      }
    }
    // Business Cards with quantity pricing
    else if (product.quantityPricing && selectedSize && selectedQuantityTier) {
      const sizeKey = selectedSize.replace(/"/g, '');
      const pricing = product.quantityPricing[sizeKey] || product.quantityPricing['2x3.5'];
      if (pricing && pricing[selectedQuantityTier]) {
        unitPrice = pricing[selectedQuantityTier];
        sizeDisplay = selectedSize;
        // Business cards: price is total for the quantity, not per unit
        // Add Spot UV cost
        const spotUVOption = product.finishOptions?.spotUV?.find(opt => opt.label === finishOptions.bcSpotUV);
        if (spotUVOption?.price) {
          unitPrice += spotUVOption.price;
        }
        return {
          unitPrice: parseFloat(unitPrice.toFixed(2)),
          quantity: selectedQuantityTier,
          total: parseFloat(unitPrice.toFixed(2)),
          size: sizeDisplay,
          isQuantityPricing: true
        };
      }
    }
    // Predefined size selected
    else if (selectedSize && product.sizes) {
      const sizeOption = product.sizes.find(s => s.size === selectedSize);
      if (sizeOption) {
        sizeDisplay = selectedSize;
        
        // Foam Core recalculation based on color spec
        if (product.category === 'foam-core' && product.finishOptions?.colorSpec?.isPricePerSqFt) {
          const [w, h] = selectedSize.split('x').map(Number);
          const area = (w * h) / 144;
          const pricePerSqFt = product.finishOptions.colorSpec.prices[finishOptions.foamCoreColorSpec] || product.pricePerSqFt;
          unitPrice = area * pricePerSqFt;
        } else {
          unitPrice = sizeOption.price;
        }

        // Add PVC finish options
        if (product.category === 'pvc' && product.finishOptions?.grommets?.prices) {
          unitPrice += product.finishOptions.grommets.prices[finishOptions.pvcGrommets] || 0;
          unitPrice += product.finishOptions.roundedCorners?.prices?.[finishOptions.pvcRoundedCorners] || 0;
        }
      }
    }

    // Add extras for Aluminum/Acrylic (non pricePerSqFt products with contourCut)
    if (!product.pricePerSqFt && product.finishOptions?.contourCut && selectedSize) {
      if (product.finishOptions.contourCut?.prices) {
        unitPrice += product.finishOptions.contourCut.prices[finishOptions.contourCut] || 0;
        unitPrice += product.finishOptions.drillHoleAndHardware?.prices?.[finishOptions.drillHole] || 0;
        unitPrice += product.finishOptions.roundedCorners?.prices?.[finishOptions.roundedCorners] || 0;
      }
    }

    if (unitPrice > 0) {
      return {
        unitPrice: parseFloat(unitPrice.toFixed(2)),
        quantity: quantity,
        total: parseFloat((unitPrice * quantity).toFixed(2)),
        size: sizeDisplay
      };
    }

    return null;
  }, [product, selectedSize, quantity, customWidth, customHeight, useCustomSize, finishOptions, isCustomSizeOnly, selectedQuantityTier]);

  // Image upload handlers
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.pdf', '.ai', '.psd'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      alert('Por favor selecciona un archivo válido (JPG, PNG, PDF, AI, PSD)');
      return;
    }
    
    // Check file size (16MB max)
    if (file.size > 16 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo: 16MB');
      return;
    }
    
    // Create preview for images
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImage({
        file: file,
        preview: file.type.startsWith('image/') ? reader.result : null,
        name: file.name,
        uploading: false,
        uploaded: false
      });
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setUploadedImage(null);
  };

  const uploadFileToServer = async (cartId) => {
    if (!uploadedImage?.file) return null;
    
    const formData = new FormData();
    formData.append('file', uploadedImage.file);
    formData.append('cart_id', cartId);
    formData.append('product_id', product.id);
    formData.append('product_name', product.name);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  const handleAddToCart = async () => {
    if (!calculatedPrice) return;

    setUploadingFile(true);
    
    // For quantity-priced products (stickers, bumper stickers, roll labels, door hangers),
    // the unitPrice IS the total price for the quantity tier, so we send quantity: 1
    // and include the actual tier quantity in the options
    const isQuantityPriced = calculatedPrice.isQuantityPricing;
    
    // First add to cart to get cart ID
    const cartResult = await addToCart({
      product_id: product.id,
      product_name: product.name,
      size: calculatedPrice.size,
      quantity: isQuantityPriced ? 1 : calculatedPrice.quantity,
      unit_price: calculatedPrice.unitPrice,
      options: {
        ...finishOptions,
        quantityTier: isQuantityPriced ? calculatedPrice.quantity : undefined,
        stickerType: calculatedPrice.stickerType,
        bumperStock: calculatedPrice.bumperStock,
        bumperLamination: calculatedPrice.bumperLamination
      },
      image_url: product.image
    });

    if (cartResult) {
      // If there's a file to upload, upload it
      if (uploadedImage?.file) {
        const cartId = localStorage.getItem('cart_id');
        if (cartId) {
          const uploadResult = await uploadFileToServer(cartId);
          if (uploadResult) {
            setUploadedImage(prev => ({
              ...prev,
              uploaded: true,
              fileId: uploadResult.file_id
            }));
          }
        }
      }
      
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    }
    
    setUploadingFile(false);
  };

  const incrementQuantity = () => setQuantity(q => q + 1);
  const decrementQuantity = () => setQuantity(q => Math.max(1, q - 1));

  // Check if size is selected (for showing upload section)
  const hasSizeSelected = selectedSize || (isCustomSizeOnly && customWidth && customHeight) || (useCustomSize && customWidth && customHeight) || (product.quantityPricing && selectedQuantityTier);

  // Check if product has any finish options
  const hasFinishOptions = product.finishOptions && (
    product.finishOptions.contourCut ||
    product.finishOptions.polePockets ||
    product.finishOptions.stock ||
    product.finishOptions.grommets ||
    product.finishOptions.colorSpec ||
    product.category === 'coroplast' ||
    product.category === 'sidewalk-signs' ||
    product.category === 'scrim-vinyl' ||
    product.category === 'mesh-banner' ||
    product.category === 'business-cards' ||
    product.category === 'door-hangers'
  );

  // Render finish options based on product type
  const renderFinishOptions = () => {
    // Business Cards finish options - check first by category
    if (product.category === 'business-cards') {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Forma</Label>
              <Select value={finishOptions.bcShape} onValueChange={(v) => updateFinishOption('bcShape', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(product.finishOptions?.shape || ['Rectangle']).map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Papel</Label>
              <Select value={finishOptions.bcStock} onValueChange={(v) => updateFinishOption('bcStock', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(product.finishOptions?.stock || ['14PT C2S']).map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Color</Label>
              <Select value={finishOptions.bcColorSpec} onValueChange={(v) => updateFinishOption('bcColorSpec', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(product.finishOptions?.colorSpec || ['4/4 (both sides)']).map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Recubrimiento</Label>
              <Select value={finishOptions.bcCoating} onValueChange={(v) => updateFinishOption('bcCoating', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(product.finishOptions?.coating || ['No Coating']).map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Spot UV</Label>
            <Select value={finishOptions.bcSpotUV} onValueChange={(v) => updateFinishOption('bcSpotUV', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(product.finishOptions?.spotUV || [{ label: 'No Spot UV', price: 0 }]).map(opt => (
                  <SelectItem key={opt.label} value={opt.label} className="text-xs">
                    {opt.label} {opt.price > 0 && `(+$${opt.price})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Door Hangers finish options
    if (product.category === 'door-hangers') {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Color</Label>
              <Select value={finishOptions.dhColorSpec} onValueChange={(v) => updateFinishOption('dhColorSpec', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(product.finishOptions?.colorSpec || ['4/4 (4 color both sides)']).map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Recubrimiento</Label>
              <Select value={finishOptions.dhCoating} onValueChange={(v) => updateFinishOption('dhCoating', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(product.finishOptions?.coating || ['No Coating']).map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Troquelado</Label>
            <Select value={finishOptions.dhDieCut} onValueChange={(v) => updateFinishOption('dhDieCut', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(product.finishOptions?.dieCut || ['Standard Die Cut']).map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded">
            Papel 16PT C2S • Agujero estándar 1.1875"
          </p>
        </div>
      );
    }

    // Aluminum/Acrylic/Aluminum HD finish options
    if (product.finishOptions?.contourCut && product.finishOptions?.drillHoleAndHardware && product.category !== 'coroplast') {
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Contour Cut</Label>
            <Select value={finishOptions.contourCut} onValueChange={(v) => updateFinishOption('contourCut', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(product.finishOptions.contourCut.options || product.finishOptions.contourCut).map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">
                    {opt} {product.finishOptions.contourCut.prices?.[opt] > 0 && `(+$${product.finishOptions.contourCut.prices[opt]})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium">Perforación/Hardware</Label>
            <Select value={finishOptions.drillHole} onValueChange={(v) => updateFinishOption('drillHole', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(product.finishOptions.drillHoleAndHardware.options || product.finishOptions.drillHoleAndHardware).map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">
                    {opt} {product.finishOptions.drillHoleAndHardware.prices?.[opt] > 0 && `(+$${product.finishOptions.drillHoleAndHardware.prices[opt]})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium">Esquinas Redondeadas</Label>
            <Select value={finishOptions.roundedCorners} onValueChange={(v) => updateFinishOption('roundedCorners', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(product.finishOptions.roundedCorners.options || product.finishOptions.roundedCorners).map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">
                    {opt} {product.finishOptions.roundedCorners.prices?.[opt] > 0 && `(+$${product.finishOptions.roundedCorners.prices[opt]})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Scrim Vinyl Banner finish options - MUST come before general polePockets check
    if (product.category === 'scrim-vinyl') {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Dobladillos</Label>
              <Select value={finishOptions.scrimHems} onValueChange={(v) => updateFinishOption('scrimHems', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.hems.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.hems.prices[opt] > 0 && `(+$${product.finishOptions.hems.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Ojales</Label>
              <Select value={finishOptions.scrimGrommets} onValueChange={(v) => updateFinishOption('scrimGrommets', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.grommets.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.grommets.prices[opt] > 0 && `(+$${product.finishOptions.grommets.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Bolsillos</Label>
              <Select value={finishOptions.scrimPolePockets} onValueChange={(v) => updateFinishOption('scrimPolePockets', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.polePockets.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.polePockets.prices[opt] > 0 && `(+$${product.finishOptions.polePockets.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Anillos D</Label>
              <Select value={finishOptions.scrimDRings} onValueChange={(v) => updateFinishOption('scrimDRings', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.dRings.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.dRings.prices[opt] > 0 && `(+$${product.finishOptions.dRings.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Velcro</Label>
              <Select value={finishOptions.scrimVelcro} onValueChange={(v) => updateFinishOption('scrimVelcro', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.velcro.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.velcro.prices[opt] > 0 && `(+$${product.finishOptions.velcro.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Cuerda</Label>
              <Select value={finishOptions.scrimRope} onValueChange={(v) => updateFinishOption('scrimRope', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.rope.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-cyan-700 bg-cyan-50 p-2 rounded">
            13oz scrim vinyl - Resistente al agua y UV
          </p>
        </div>
      );
    }

    // Mesh Banner finish options - MUST come before general polePockets check
    if (product.category === 'mesh-banner') {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Dobladillos</Label>
              <Select value={finishOptions.meshHems} onValueChange={(v) => updateFinishOption('meshHems', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.hems.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Ojales</Label>
              <Select value={finishOptions.meshGrommets} onValueChange={(v) => updateFinishOption('meshGrommets', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.grommets.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.grommets.prices[opt] > 0 && `(+$${product.finishOptions.grommets.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Bolsillos para Poste</Label>
            <Select value={finishOptions.meshPolePockets} onValueChange={(v) => updateFinishOption('meshPolePockets', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {product.finishOptions.polePockets.options.map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">
                    {opt} {product.finishOptions.polePockets.prices[opt] > 0 && `(+$${product.finishOptions.polePockets.prices[opt]})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-green-700 bg-green-50 p-2 rounded">
            8oz mesh vinyl - 37% flujo de aire - Ideal para exteriores con viento
          </p>
        </div>
      );
    }

    // Canvas Banner finish options (Artist Canvas - has threadColor)
    if (product.finishOptions?.polePockets && product.finishOptions?.threadColor) {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Dobladillos</Label>
              <Select value={finishOptions.hems} onValueChange={(v) => updateFinishOption('hems', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.hems.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Ojales</Label>
              <Select value={finishOptions.grommets} onValueChange={(v) => updateFinishOption('grommets', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.grommets.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Color de Hilo</Label>
              <Select value={finishOptions.threadColor} onValueChange={(v) => updateFinishOption('threadColor', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.threadColor.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Bolsillos</Label>
              <Select value={finishOptions.polePockets} onValueChange={(v) => updateFinishOption('polePockets', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.polePockets.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.polePockets.prices[opt] > 0 && `(+$${product.finishOptions.polePockets.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );
    }

    // PVC finish options
    if (product.category === 'pvc' && product.finishOptions?.grommets) {
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Color</Label>
            <Select value={finishOptions.pvcColorSpec} onValueChange={(v) => updateFinishOption('pvcColorSpec', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {product.finishOptions.colorSpec.options.map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Ojales</Label>
              <Select value={finishOptions.pvcGrommets} onValueChange={(v) => updateFinishOption('pvcGrommets', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.grommets.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.grommets.prices[opt] > 0 && `(+$${product.finishOptions.grommets.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Esquinas</Label>
              <Select value={finishOptions.pvcRoundedCorners} onValueChange={(v) => updateFinishOption('pvcRoundedCorners', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.finishOptions.roundedCorners.options.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-xs">
                      {opt} {product.finishOptions.roundedCorners.prices?.[opt] > 0 && `(+$${product.finishOptions.roundedCorners.prices[opt]})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );
    }

    // Foam Core finish options
    if (product.category === 'foam-core' && product.finishOptions?.colorSpec?.isPricePerSqFt) {
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Color (afecta precio)</Label>
            <Select value={finishOptions.foamCoreColorSpec} onValueChange={(v) => updateFinishOption('foamCoreColorSpec', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {product.finishOptions.colorSpec.options.map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">
                    {opt} - ${product.finishOptions.colorSpec.prices[opt]}/pie²
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded">
            ⚠️ No resistente al agua. Solo uso interior.
          </p>
        </div>
      );
    }

    // Gator Board finish options
    if (product.finishOptions?.stock) {
      return (
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-medium">Material</Label>
            <Select value={finishOptions.stock} onValueChange={(v) => updateFinishOption('stock', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {product.finishOptions.stock.options.map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium">Color</Label>
            <Select value={finishOptions.colorSpec} onValueChange={(v) => updateFinishOption('colorSpec', v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {product.finishOptions.colorSpec.options.map(opt => (
                  <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Coroplast finish options
    if (product.category === 'coroplast') {
      return (
        <div className="space-y-3">
          <div className="p-2 bg-orange-50 rounded text-xs">
            <p className="font-medium text-orange-800">Descuentos por volumen:</p>
            <p className="text-orange-700">6+: 9% | 26+: 13% | 51+: 18% | 76+: 23%</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Color</Label>
              <Select value={finishOptions.coroplastColorSpec} onValueChange={(v) => updateFinishOption('coroplastColorSpec', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4/4 (4 color both sides)" className="text-xs">4/4 (ambos lados)</SelectItem>
                  <SelectItem value="4/0 (4 color front)" className="text-xs">4/0 (solo frente)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Acanalado</Label>
              <Select value={finishOptions.coroplastFluteDirection} onValueChange={(v) => updateFinishOption('coroplastFluteDirection', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vertical" className="text-xs">Vertical</SelectItem>
                  <SelectItem value="Horizontal" className="text-xs">Horizontal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Ojales</Label>
              <Select value={finishOptions.coroplastGrommets} onValueChange={(v) => updateFinishOption('coroplastGrommets', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="None" className="text-xs">Ninguno</SelectItem>
                  <SelectItem value="All 4 Corners" className="text-xs">4 Esquinas (+$0.45)</SelectItem>
                  <SelectItem value="Top Corners Only" className="text-xs">2 Superiores (+$0.45)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">H-Stakes</Label>
              <Select value={finishOptions.coroplastHStakes} onValueChange={(v) => updateFinishOption('coroplastHStakes', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="No" className="text-xs">No</SelectItem>
                  <SelectItem value="Yes" className="text-xs">Sí (+$2.50)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );
    }

    // Sidewalk Signs finish options
    if (product.category === 'sidewalk-signs') {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-medium">Color</Label>
              <Select value={finishOptions.sidewalkColorSpec} onValueChange={(v) => updateFinishOption('sidewalkColorSpec', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4/0 (4 color front)" className="text-xs">4/0 (solo frente)</SelectItem>
                  <SelectItem value="4/4 (4 color both sides)" className="text-xs">4/4 (ambos lados)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Acanalado</Label>
              <Select value={finishOptions.sidewalkFluteDirection} onValueChange={(v) => updateFinishOption('sidewalkFluteDirection', v)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vertical" className="text-xs">Vertical</SelectItem>
                  <SelectItem value="Horizontal" className="text-xs">Horizontal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded">
            Incluye marco de aluminio y 2 insertos de Coroplast
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <Card 
      className="group overflow-hidden border-2 border-gray-200 hover:border-cyan-400 shadow-lg hover:shadow-xl transition-all duration-300"
      data-testid={`product-card-${product.id}`}
    >
      {/* Product Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={product.category === 'stickers' && selectedStickerType 
            ? product.stickerTypes.find(t => t.type === selectedStickerType)?.image || product.image
            : product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3 bg-white rounded-full px-2 py-1 shadow-md">
          <span className="text-xs font-semibold text-gray-600">Desde</span>
          <div className="text-sm font-bold text-cyan-600">
            {product.pricePerSqFt ? `$${product.pricePerSqFt}/pie²` : 
             product.stickerTypes ? `$${product.basePrice}` :
             product.quantityPricing ? `$${Object.values(Object.values(product.quantityPricing)?.[0] || {})[0] || product.basePrice}` :
             `$${product.basePrice || product.sizes?.[0]?.price || '0'}`}
          </div>
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Product Info */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-cyan-600 transition-colors">
            {t(`productNames.${product.id}.name`, product.name)}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2 mt-1">
            {t(`productNames.${product.id}.description`, product.description)}
          </p>
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-1">
          {product.features.slice(0, 2).map((feature, idx) => (
            <span key={idx} className="text-xs bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded-full">
              {feature}
            </span>
          ))}
        </div>

        {/* Size Selection */}
        {product.category !== 'stickers' && product.category !== 'bumper-stickers' && product.category !== 'roll-labels' && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-700">{t('products.size')}</Label>
          
          {/* Products with predefined sizes only (not quantity pricing products) */}
          {!product.pricePerSqFt && !product.quantityPricing && product.sizes && product.sizes[0]?.price && (
            <Select value={selectedSize} onValueChange={setSelectedSize}>
              <SelectTrigger className="w-full h-9" data-testid={`size-select-${product.id}`}>
                <SelectValue placeholder={t('products.selectSize')} />
              </SelectTrigger>
              <SelectContent>
                {product.sizes.map(sizeOpt => (
                  <SelectItem key={sizeOpt.size} value={sizeOpt.size}>
                    <span className="font-medium">{sizeOpt.size}"</span>
                    <span className="text-cyan-600 ml-2">${(sizeOpt.price || 0).toFixed(2)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Products with quantity pricing - show size selector without prices */}
          {product.quantityPricing && product.sizes && (
            <Select value={selectedSize} onValueChange={setSelectedSize}>
              <SelectTrigger className="w-full h-9" data-testid={`size-select-${product.id}`}>
                <SelectValue placeholder={t('products.selectSize')} />
              </SelectTrigger>
              <SelectContent>
                {product.sizes.map(sizeOpt => (
                  <SelectItem key={sizeOpt.size} value={sizeOpt.size}>
                    <span className="font-medium">{sizeOpt.label || sizeOpt.size}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Products with both predefined and custom sizes (PVC, Foam Core) */}
          {hasBothSizeOptions && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={() => setUseCustomSize(false)}
                  className={`flex-1 text-xs py-1.5 rounded transition-colors ${
                    !useCustomSize ? 'bg-cyan-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('products.fixedSizes')}
                </button>
                <button
                  onClick={() => { setUseCustomSize(true); setSelectedSize(''); }}
                  className={`flex-1 text-xs py-1.5 rounded transition-colors ${
                    useCustomSize ? 'bg-cyan-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('products.custom')}
                </button>
              </div>
              
              {!useCustomSize ? (
                <Select value={selectedSize} onValueChange={setSelectedSize}>
                  <SelectTrigger className="w-full h-9" data-testid={`size-select-${product.id}`}>
                    <SelectValue placeholder={t('products.selectSize')} />
                  </SelectTrigger>
                  <SelectContent>
                    {product.sizes.map(sizeOpt => (
                      <SelectItem key={sizeOpt.size} value={sizeOpt.size}>
                        <span className="font-medium">{sizeOpt.size}"</span>
                        <span className="text-cyan-600 ml-2">${(sizeOpt.price || 0).toFixed(2)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      placeholder={`Ancho (${product.customSize?.widthRange?.min || 6}-${product.customSize?.widthRange?.max || 96}")`}
                      className="w-full h-9 px-2 text-sm border rounded focus:border-cyan-500 focus:outline-none"
                      data-testid={`custom-width-${product.id}`}
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      placeholder={`Alto (${product.customSize?.heightRange?.min || 6}-${product.customSize?.heightRange?.max || 96}")`}
                      className="w-full h-9 px-2 text-sm border rounded focus:border-cyan-500 focus:outline-none"
                      data-testid={`custom-height-${product.id}`}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Products with custom size only (Canvas Banners, Aluminum HD, Gator Board, Coroplast) */}
          {isCustomSizeOnly && (
            <div className="space-y-2">
              <p className="text-xs text-cyan-600">${product.pricePerSqFt}/pie² • Medidas personalizadas</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <input
                    type="number"
                    value={customWidth}
                    onChange={(e) => setCustomWidth(e.target.value)}
                    placeholder={`Ancho (${product.customSize?.widthRange?.min || 6}-${product.customSize?.widthRange?.max || 96}")`}
                    className="w-full h-9 px-2 text-sm border rounded focus:border-cyan-500 focus:outline-none"
                    data-testid={`custom-width-${product.id}`}
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={customHeight}
                    onChange={(e) => setCustomHeight(e.target.value)}
                    placeholder={`Alto (${product.customSize?.heightRange?.min || 6}-${product.customSize?.heightRange?.max || 96}")`}
                    className="w-full h-9 px-2 text-sm border rounded focus:border-cyan-500 focus:outline-none"
                    data-testid={`custom-height-${product.id}`}
                  />
                </div>
              </div>
              {product.customSize && (
                <p className="text-xs text-gray-500">
                  Min: {product.customSize.widthRange.min}x{product.customSize.heightRange.min}" | 
                  Max: {product.customSize.widthRange.max}x{product.customSize.heightRange.max}"
                </p>
              )}
            </div>
          )}
        </div>
        )}

        {/* STICKERS PRODUCT - Dynamic selection UI */}
        {product.category === 'stickers' && product.stickerTypes && (
          <div className="space-y-3">
            {/* Step 1: Sticker Type */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Tipo de Sticker</Label>
              <Select 
                value={selectedStickerType || ''} 
                onValueChange={(v) => {
                  setSelectedStickerType(v);
                  setSelectedSize('');
                  setStickerStock('');
                  setStickerCoating('');
                  setSelectedQuantityTier(null);
                }}
              >
                <SelectTrigger className="w-full h-9" data-testid={`sticker-type-select-${product.id}`}>
                  <SelectValue placeholder="Selecciona tipo de sticker..." />
                </SelectTrigger>
                <SelectContent>
                  {product.stickerTypes.map(type => (
                    <SelectItem key={type.type} value={type.type}>
                      <span className="font-medium">{type.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedStickerType && (
                <p className="text-xs text-gray-500">
                  {product.stickerTypes.find(t => t.type === selectedStickerType)?.description}
                </p>
              )}
            </div>

            {/* Step 2: Size (dynamic based on type) */}
            {selectedStickerType && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Tamaño</Label>
                <Select 
                  value={selectedSize} 
                  onValueChange={(v) => {
                    setSelectedSize(v);
                    setSelectedQuantityTier(null);
                  }}
                >
                  <SelectTrigger className="w-full h-9" data-testid={`size-select-${product.id}`}>
                    <SelectValue placeholder="Selecciona tamaño..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stickerTypes.find(t => t.type === selectedStickerType)?.sizes.map(sizeOpt => (
                      <SelectItem key={sizeOpt.size} value={sizeOpt.size}>
                        {sizeOpt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 3: Stock (dynamic based on type) */}
            {selectedStickerType && selectedSize && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Material</Label>
                <Select value={stickerStock} onValueChange={setStickerStock}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona material..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stickerTypes.find(t => t.type === selectedStickerType)?.stock.map(stock => (
                      <SelectItem key={stock} value={stock}>{stock}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 4: Coating (dynamic based on type) */}
            {selectedStickerType && selectedSize && stickerStock && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Recubrimiento</Label>
                <Select value={stickerCoating} onValueChange={setStickerCoating}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona recubrimiento..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stickerTypes.find(t => t.type === selectedStickerType)?.coating.map(coat => (
                      <SelectItem key={coat} value={coat}>{coat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 5: Quantity */}
            {selectedStickerType && selectedSize && stickerStock && stickerCoating && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Cantidad</Label>
                <Select value={selectedQuantityTier?.toString() || ''} onValueChange={(v) => setSelectedQuantityTier(parseInt(v))}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona cantidad..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const stickerType = product.stickerTypes.find(t => t.type === selectedStickerType);
                      const sizeKey = selectedSize.replace(/"/g, '');
                      const pricing = stickerType?.quantityPricing[sizeKey] || stickerType?.quantityPricing['default'] || {};
                      return Object.entries(pricing).map(([qty, price]) => (
                        <SelectItem key={qty} value={qty}>
                          <span className="font-medium">{qty} unidades</span>
                          <span className="text-cyan-600 ml-2">${(price || 0).toFixed(2)}</span>
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 6: File Upload */}
            {selectedStickerType && selectedSize && stickerStock && stickerCoating && selectedQuantityTier && (
              <div className="space-y-1">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Sube tu Diseño
                </Label>
                {!uploadedImage ? (
                  <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed border-cyan-300 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-colors">
                    <Upload className="w-5 h-5 text-cyan-400" />
                    <span className="text-xs text-gray-500 mt-1">Click para subir</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*,.pdf"
                      onChange={handleImageUpload}
                      data-testid={`upload-${product.id}`}
                    />
                  </label>
                ) : (
                  <div className="relative h-20 border-2 border-green-300 rounded-lg overflow-hidden bg-green-50">
                    {uploadedImage.preview ? (
                      <img src={uploadedImage.preview} alt="Preview" className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xs text-gray-600 truncate px-2">{uploadedImage.name}</span>
                      </div>
                    )}
                    <button
                      onClick={removeImage}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-1 left-1 flex items-center gap-1 bg-green-500 text-white px-2 py-0.5 rounded text-xs">
                      <CheckCircle2 className="w-3 h-3" /> Listo
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400">PDF, JPG, PNG • 300dpi • CMYK</p>
              </div>
            )}
          </div>
        )}

        {/* BUMPER STICKERS PRODUCT - Dynamic selection UI */}
        {product.category === 'bumper-stickers' && product.stockOptions && (
          <div className="space-y-3">
            {/* Step 1: Size */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Tamaño</Label>
              <Select 
                value={selectedSize} 
                onValueChange={(v) => {
                  setSelectedSize(v);
                  setBumperStock('');
                  setBumperLamination('');
                  setSelectedQuantityTier(null);
                }}
              >
                <SelectTrigger className="w-full h-9" data-testid={`size-select-${product.id}`}>
                  <SelectValue placeholder="Selecciona tamaño..." />
                </SelectTrigger>
                <SelectContent>
                  {product.sizes.map(sizeOpt => (
                    <SelectItem key={sizeOpt.size} value={sizeOpt.size}>
                      {sizeOpt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Stock */}
            {selectedSize && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Material</Label>
                <Select 
                  value={bumperStock} 
                  onValueChange={(v) => {
                    setBumperStock(v);
                    setBumperLamination('');
                    setSelectedQuantityTier(null);
                  }}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona material..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stockOptions.map(opt => (
                      <SelectItem key={opt.stock} value={opt.stock}>{opt.stock}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 3: Lamination (dynamic based on stock) */}
            {selectedSize && bumperStock && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Laminación</Label>
                <Select 
                  value={bumperLamination} 
                  onValueChange={(v) => {
                    setBumperLamination(v);
                    setSelectedQuantityTier(null);
                  }}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona laminación..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stockOptions.find(s => s.stock === bumperStock)?.availableLamination.map(lam => (
                      <SelectItem key={lam} value={lam}>{lam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {product.stockOptions.find(s => s.stock === bumperStock)?.requiresLamination && (
                  <p className="text-xs text-amber-600">* Laminación requerida para este material</p>
                )}
              </div>
            )}

            {/* Step 4: Quantity (dynamic based on stock and lamination) */}
            {selectedSize && bumperStock && bumperLamination && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Cantidad</Label>
                <Select value={selectedQuantityTier?.toString() || ''} onValueChange={(v) => setSelectedQuantityTier(parseInt(v))}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona cantidad..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const stockOption = product.stockOptions.find(s => s.stock === bumperStock);
                      const pricing = stockOption?.quantityPricing[bumperLamination] || stockOption?.quantityPricing['default'] || {};
                      return Object.entries(pricing).map(([qty, price]) => (
                        <SelectItem key={qty} value={qty}>
                          <span className="font-medium">{qty} unidades</span>
                          <span className="text-cyan-600 ml-2">${(price || 0).toFixed(2)}</span>
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 5: File Upload */}
            {selectedSize && bumperStock && bumperLamination && selectedQuantityTier && (
              <div className="space-y-1">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Sube tu Diseño
                </Label>
                {!uploadedImage ? (
                  <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed border-cyan-300 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-colors">
                    <Upload className="w-5 h-5 text-cyan-400" />
                    <span className="text-xs text-gray-500 mt-1">Click para subir</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*,.pdf,.eps,.tiff"
                      onChange={handleImageUpload}
                      data-testid={`upload-${product.id}`}
                    />
                  </label>
                ) : (
                  <div className="relative h-20 border-2 border-green-300 rounded-lg overflow-hidden bg-green-50">
                    {uploadedImage.preview ? (
                      <img src={uploadedImage.preview} alt="Preview" className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xs text-gray-600 truncate px-2">{uploadedImage.name}</span>
                      </div>
                    )}
                    <button
                      onClick={removeImage}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-1 left-1 flex items-center gap-1 bg-green-500 text-white px-2 py-0.5 rounded text-xs">
                      <CheckCircle2 className="w-3 h-3" /> Listo
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400">PDF, JPG, EPS, TIFF • 300dpi • CMYK • 1" bleed</p>
              </div>
            )}
          </div>
        )}

        {/* ROLL LABELS PRODUCT - Dynamic selection UI */}
        {product.category === 'roll-labels' && product.stockOptions && product.pricingGroups && (
          <div className="space-y-3">
            {/* Step 1: Size */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Tamaño</Label>
              <Select 
                value={selectedSize} 
                onValueChange={(v) => {
                  setSelectedSize(v);
                  setRollStock('');
                  setRollColorspec('');
                  setRollLamination('');
                  setRollUnwind('');
                  setRollTurnaround('');
                  setSelectedQuantityTier(null);
                }}
              >
                <SelectTrigger className="w-full h-9" data-testid={`size-select-${product.id}`}>
                  <SelectValue placeholder="Selecciona tamaño..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {product.sizes.map(sizeOpt => (
                    <SelectItem key={sizeOpt.size} value={sizeOpt.size}>
                      {sizeOpt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Stock */}
            {selectedSize && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Material</Label>
                <Select 
                  value={rollStock} 
                  onValueChange={(v) => {
                    setRollStock(v);
                    setRollColorspec('');
                    setRollLamination('');
                    setSelectedQuantityTier(null);
                  }}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona material..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stockOptions.map(opt => (
                      <SelectItem key={opt.stock} value={opt.stock}>{opt.stock}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 3: Colorspec (dynamic based on stock) */}
            {selectedSize && rollStock && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Especificación de Color</Label>
                <Select 
                  value={rollColorspec} 
                  onValueChange={(v) => {
                    setRollColorspec(v);
                    setRollLamination('');
                    setSelectedQuantityTier(null);
                  }}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona color..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stockOptions.find(s => s.stock === rollStock)?.availableColorspec.map(cs => (
                      <SelectItem key={cs} value={cs}>{cs}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {product.stockOptions.find(s => s.stock === rollStock)?.whiteMaskAvailable && (
                  <p className="text-xs text-blue-600">* Tinta blanca disponible para este material</p>
                )}
              </div>
            )}

            {/* Step 4: Lamination (dynamic based on stock) */}
            {selectedSize && rollStock && rollColorspec && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Laminación</Label>
                <Select 
                  value={rollLamination} 
                  onValueChange={(v) => {
                    setRollLamination(v);
                    setSelectedQuantityTier(null);
                  }}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona laminación..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.stockOptions.find(s => s.stock === rollStock)?.availableLamination.map(lam => (
                      <SelectItem key={lam} value={lam}>{lam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 5: Unwind Option */}
            {selectedSize && rollStock && rollColorspec && rollLamination && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Dirección de Desenrollado</Label>
                <Select value={rollUnwind} onValueChange={setRollUnwind}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona dirección..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.unwindOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 6: Turnaround */}
            {selectedSize && rollStock && rollColorspec && rollLamination && rollUnwind && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Tiempo de Entrega</Label>
                <Select value={rollTurnaround} onValueChange={setRollTurnaround}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona entrega..." />
                  </SelectTrigger>
                  <SelectContent>
                    {product.turnaroundOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 7: Quantity */}
            {selectedSize && rollStock && rollColorspec && rollLamination && rollUnwind && rollTurnaround && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Cantidad</Label>
                <Select value={selectedQuantityTier?.toString() || ''} onValueChange={(v) => setSelectedQuantityTier(parseInt(v))}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Selecciona cantidad..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const stockOption = product.stockOptions.find(s => s.stock === rollStock);
                      const pricingGroup = product.pricingGroups[stockOption?.pricingGroup];
                      if (!pricingGroup) return null;
                      
                      const hasWhiteMask = rollColorspec === '4/0 with White Mask';
                      const hasSilk = rollLamination === 'Silk';
                      
                      let pricing = pricingGroup.base;
                      if (hasWhiteMask && hasSilk && pricingGroup.whiteMaskWithSilk) {
                        pricing = pricingGroup.whiteMaskWithSilk;
                      }
                      
                      return Object.entries(pricing).map(([qty, price]) => {
                        let displayPrice = price;
                        if (hasWhiteMask && !hasSilk && pricingGroup.whiteMaskSurcharge) {
                          displayPrice = price * (1 + pricingGroup.whiteMaskSurcharge);
                        }
                        return (
                          <SelectItem key={qty} value={qty}>
                            <span className="font-medium">{qty} unidades</span>
                            <span className="text-cyan-600 ml-2">${displayPrice.toFixed(2)}</span>
                          </SelectItem>
                        );
                      });
                    })()}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 8: File Upload */}
            {selectedSize && rollStock && rollColorspec && rollLamination && rollUnwind && rollTurnaround && selectedQuantityTier && (
              <div className="space-y-1">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Sube tu Diseño
                </Label>
                {!uploadedImage ? (
                  <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed border-cyan-300 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-colors">
                    <Upload className="w-5 h-5 text-cyan-400" />
                    <span className="text-xs text-gray-500 mt-1">Click para subir</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*,.pdf,.eps,.tiff"
                      onChange={handleImageUpload}
                      data-testid={`upload-${product.id}`}
                    />
                  </label>
                ) : (
                  <div className="relative h-20 border-2 border-green-300 rounded-lg overflow-hidden bg-green-50">
                    {uploadedImage.preview ? (
                      <img src={uploadedImage.preview} alt="Preview" className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xs text-gray-600 truncate px-2">{uploadedImage.name}</span>
                      </div>
                    )}
                    <button
                      onClick={removeImage}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-1 left-1 flex items-center gap-1 bg-green-500 text-white px-2 py-0.5 rounded text-xs">
                      <CheckCircle2 className="w-3 h-3" /> Listo
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400">PDF, JPG, EPS, TIFF • 300dpi • CMYK • 0.125" bleed</p>
              </div>
            )}
          </div>
        )}

        {/* Quantity Selection - Different for quantity pricing products (excluding stickers, bumper-stickers, roll-labels) */}
        {product.quantityPricing && product.category !== 'stickers' && product.category !== 'bumper-stickers' && product.category !== 'roll-labels' ? (
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700">{t('products.selectQuantity') || 'Selecciona Cantidad'}</Label>
            <Select value={selectedQuantityTier?.toString() || ''} onValueChange={(v) => setSelectedQuantityTier(parseInt(v))}>
              <SelectTrigger className="w-full h-9">
                <SelectValue placeholder={t('products.selectQuantityPlaceholder') || 'Selecciona cantidad...'} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(
                  (selectedSize ? product.quantityPricing[selectedSize.replace(/"/g, '')] : null) || 
                  Object.values(product.quantityPricing)?.[0] || 
                  {}
                ).map(([qty, price]) => (
                  <SelectItem key={qty} value={qty}>
                    <span className="font-medium">{qty} unidades</span>
                    <span className="text-cyan-600 ml-2">${(price || 0).toFixed(2)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : product.category !== 'stickers' && product.category !== 'bumper-stickers' && product.category !== 'roll-labels' && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700">{t('products.quantity')}</Label>
            <div className="flex items-center gap-2">
              <button
                onClick={decrementQuantity}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                data-testid={`qty-minus-${product.id}`}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-12 text-center font-semibold" data-testid={`qty-display-${product.id}`}>
                {quantity}
              </span>
              <button
                onClick={incrementQuantity}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                data-testid={`qty-plus-${product.id}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Image Upload Section - Dual upload for business cards */}
        {hasSizeSelected && product.requiresDualUpload && (
          <div className="space-y-3">
            {/* Front Upload */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                <Upload className="w-3 h-3 text-cyan-600" />
                {t('products.uploadFront') || 'Subir Frente'}
              </Label>
              {!uploadedImage ? (
                <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-cyan-300 rounded-lg cursor-pointer hover:bg-cyan-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-cyan-500" />
                    <span className="text-xs text-gray-600">{t('products.clickToUpload')}</span>
                  </div>
                  <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleImageUpload} />
                </label>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-cyan-50 rounded border border-cyan-200">
                  {uploadedImage.preview && <img src={uploadedImage.preview} alt="Front" className="w-10 h-10 object-cover rounded" />}
                  <span className="text-xs flex-1 truncate">{uploadedImage.name}</span>
                  <button onClick={() => setUploadedImage(null)} className="p-1 hover:bg-cyan-100 rounded"><X className="w-3 h-3 text-red-500" /></button>
                </div>
              )}
            </div>
            {/* Back Upload */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                <Upload className="w-3 h-3 text-purple-600" />
                {t('products.uploadBack') || 'Subir Reverso'}
              </Label>
              {!uploadedImageBack ? (
                <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-gray-600">{t('products.clickToUpload')}</span>
                  </div>
                  <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
                      setUploadedImageBack({ file, name: file.name, preview });
                    }
                  }} />
                </label>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-purple-50 rounded border border-purple-200">
                  {uploadedImageBack.preview && <img src={uploadedImageBack.preview} alt="Back" className="w-10 h-10 object-cover rounded" />}
                  <span className="text-xs flex-1 truncate">{uploadedImageBack.name}</span>
                  <button onClick={() => setUploadedImageBack(null)} className="p-1 hover:bg-purple-100 rounded"><X className="w-3 h-3 text-red-500" /></button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Standard Image Upload Section (non-dual, excluding stickers, bumper-stickers, roll-labels) */}
        {hasSizeSelected && !product.requiresDualUpload && product.category !== 'stickers' && product.category !== 'bumper-stickers' && product.category !== 'roll-labels' && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Upload className="w-4 h-4 text-green-600" />
              {t('products.uploadImage')}
            </Label>
            
            {!uploadedImage ? (
              <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-green-300 rounded-lg cursor-pointer hover:bg-green-50 transition-colors">
                <div className="flex flex-col items-center justify-center py-2">
                  <Upload className="w-6 h-6 text-green-500 mb-1" />
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">{t('products.clickToUpload')}</span>
                  </p>
                  <p className="text-xs text-gray-400">PNG, JPG, PDF</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf"
                  onChange={handleImageUpload}
                  data-testid={`file-upload-${product.id}`}
                />
              </label>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                {uploadedImage.preview && (
                  <img 
                    src={uploadedImage.preview} 
                    alt="Preview" 
                    className="w-12 h-12 object-cover rounded"
                  />
                )}
                {!uploadedImage.preview && (
                  <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
                    <Upload className="w-5 h-5 text-green-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {uploadedImage.name}
                  </p>
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {uploadedImage.uploaded ? 'Subido al servidor' : 'Archivo listo'}
                  </p>
                </div>
                <button
                  onClick={removeImage}
                  className="p-1 hover:bg-red-100 rounded transition-colors"
                  data-testid={`remove-file-${product.id}`}
                >
                  <X className="w-4 h-4 text-red-500" />
                </button>
              </div>
            )}
            
            {/* File requirements note */}
            {product.filePreparation && !uploadedImage && (
              <p className="text-xs text-gray-500">
                {product.filePreparation.resolution} • {product.filePreparation.colorMode}
              </p>
            )}
          </div>
        )}

        {/* Advanced Options Accordion */}
        {hasFinishOptions && (selectedSize || (isCustomSizeOnly && customWidth && customHeight) || (useCustomSize && customWidth && customHeight)) && (
          <Accordion type="single" collapsible className="border rounded-lg">
            <AccordionItem value="options" className="border-0">
              <AccordionTrigger className="px-3 py-2 text-sm font-medium hover:no-underline">
                <div className="flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-cyan-600" />
                  <span>{t('products.advancedOptions')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                {renderFinishOptions()}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Price Display */}
        {calculatedPrice && (
          <div className="p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-600">{t('products.unitPrice')}</p>
                <p className="text-sm font-semibold text-gray-800">${calculatedPrice.unitPrice.toFixed(2)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">{t('products.total')} ({calculatedPrice.quantity} {t('products.units')})</p>
                <p className="text-xl font-bold text-cyan-600">${calculatedPrice.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Add to Cart Button */}
        <Button
          onClick={handleAddToCart}
          disabled={!calculatedPrice || cartLoading || addedToCart || uploadingFile}
          className={`w-full transition-all duration-300 ${
            addedToCart 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600'
          }`}
          data-testid={`add-to-cart-${product.id}`}
        >
          {uploadingFile ? (
            <>
              <svg className="animate-spin w-4 h-4 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              {t('products.uploadingFile')}
            </>
          ) : addedToCart ? (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {t('products.added')}
            </>
          ) : (
            <>
              <ShoppingCart className="w-4 h-4 mr-2" />
              {t('products.addToCart')}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
